import React, { useState } from 'react';
import { tickerAPI } from '../../services/api';
import { resolveShortcodes, extractYouTubeId, stripYouTubeUrls, getTickerPhotoUrl } from '../../utils/tickerUtils';
import { getImageUrl } from '../../services/api';
import {
    getTickerEventPlayerOutKey,
    getTickerEventPlayerInKey,
    revertSubstitutionFromLineup,
} from '../../utils/tickerLineupUtils';
import TickerEventEditPanel, { isStatTickerEvent } from './TickerEventEditPanel';

const TYPE = {
    1: { icon: null, cls: 'ticker-event--info' },
    2: { icon: '⚽', cls: 'ticker-event--goal' },
    3: { icon: '🟨', cls: 'ticker-event--yellow' },
    4: { icon: '🟥', cls: 'ticker-event--red' },
    5: { icon: '🔄', cls: 'ticker-event--sub' },
    6: { icon: null, cls: 'ticker-event--phase' },
    7: { icon: null, cls: 'ticker-event--phase' },
    8: { icon: null, cls: 'ticker-event--chance' },
    9: { icon: null, cls: 'ticker-event--phase' },
    10: { icon: '', cls: 'ticker-event--mood' },
    11: { icon: '📺', cls: 'ticker-event--video' },
    12: { icon: '📷', cls: 'ticker-event--photo' },
};

const SHOW_ICON = new Set([2, 3, 4, 5, 11, 12]);

const cleanText = (t) => String(t || '')
    .replace(/^\s*(⚽|🟨|🟥|🔄|⏸|🏁|💥|📊|🎉|ℹ️)\s*/u, '')
    .replace(/\[H\]\s*/g, '')
    .replace(/\[G\]\s*/g, '')
    .trim();

export default function TickerEventCard({
    event,
    onDelete,
    onUpdate,
    hideMinute,
    compact = true,
    isNew = false,
    game,
    lineup,
    events,
    availablePlayers,
    meta,
    onLineupChange,
}) {
    const [editingText, setEditingText] = useState(false);
    const [editingStat, setEditingStat] = useState(false);
    const [editText, setEditText] = useState(event.textRaw || event.misc || '');
    const [saving, setSaving] = useState(false);

    const cfg = TYPE[event.type] || TYPE[1];
    const stored = event.textPublished || event.textResolved || event.textRaw || event.misc || '';
    const { textResolved } = resolveShortcodes(stored, game, availablePlayers, lineup);
    const raw = textResolved || stored;
    const youtubeId = extractYouTubeId(raw);
    const photoPath = getTickerPhotoUrl(event);
    const photoUrl = photoPath ? getImageUrl(photoPath) : null;
    const displayText = cleanText(youtubeId ? stripYouTubeUrls(raw) : raw);
    const minute = event.minute || 0;
    const extra = event.minuteExtra > 0 ? `+${event.minuteExtra}` : '';
    const isDraft = event.published === false;
    const isStatEvent = isStatTickerEvent(event);

    const handleSaveTextEdit = async () => {
        setSaving(true);
        try {
            const { textResolved } = resolveShortcodes(editText, game, availablePlayers, lineup);
            const res = await tickerAPI.update(event._id, {
                textRaw: editText,
                textResolved,
                textPublished: textResolved,
            });
            onUpdate(res.data?.data || { ...event, textRaw: editText, textResolved, textPublished: textResolved });
            setEditingText(false);
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    const handleTogglePublish = async () => {
        try {
            const res = await tickerAPI.publish(event._id);
            onUpdate(res.data?.data || res.data);
        } catch (err) { console.error(err); }
    };

    const handleToggleReportFlag = async (field) => {
        try {
            const newFlags = { ...event.reportFlags, [field]: !event.reportFlags?.[field] };
            await tickerAPI.setReportFlag(event._id, newFlags);
            onUpdate({ ...event, reportFlags: newFlags });
        } catch (err) { console.error(err); }
    };

    const handleDelete = async () => {
        const needsConfirm = [2, 3, 4, 5].includes(event.type);
        if (needsConfirm && !window.confirm('Eintrag löschen?')) return;
        try {
            if (event.type === 5 && onLineupChange && lineup?.length) {
                const oldOut = getTickerEventPlayerOutKey(event);
                const oldIn = getTickerEventPlayerInKey(event);
                if (oldOut && oldIn) {
                    onLineupChange(revertSubstitutionFromLineup(lineup, oldOut, oldIn));
                }
            }
            await tickerAPI.delete(event._id);
            onDelete(event._id);
        } catch (err) { console.error(err); }
    };

    const startEdit = () => {
        if (isStatEvent && game && lineup) {
            setEditingStat(true);
            setEditingText(false);
            return;
        }
        setEditText(event.textRaw || event.misc || '');
        setEditingText(true);
        setEditingStat(false);
    };

    return (
        <article className={`ticker-event ${cfg.cls} ${compact ? 'ticker-event--compact' : ''} ${isDraft ? 'ticker-event--draft' : ''} ${isNew ? 'ticker-event--new' : ''}`}>
            {!hideMinute && (
                <div className="ticker-event-minute">
                    {minute > 0 ? `${minute}${extra}'` : ''}
                </div>
            )}

            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                    {SHOW_ICON.has(event.type) && cfg.icon && (
                        <span className="ticker-event-icon flex-none">{cfg.icon}</span>
                    )}
                    <div className="flex-1 min-w-0">
                        {youtubeId && (
                            <div className="mb-2 rounded-lg overflow-hidden aspect-video max-w-md">
                                <iframe
                                    src={`https://www.youtube.com/embed/${youtubeId}`}
                                    title="YouTube"
                                    className="w-full h-full"
                                    allowFullScreen
                                />
                            </div>
                        )}

                        {photoUrl && (
                            <div className="ticker-event-photo mb-2">
                                <img src={photoUrl} alt={displayText || 'Ticker Foto'} loading="lazy" />
                            </div>
                        )}

                        {editingStat ? (
                            <TickerEventEditPanel
                                event={event}
                                game={game}
                                lineup={lineup}
                                events={events}
                                availablePlayers={availablePlayers}
                                meta={meta}
                                onLineupChange={onLineupChange}
                                onSaved={(saved) => {
                                    onUpdate(saved);
                                    setEditingStat(false);
                                }}
                                onCancel={() => setEditingStat(false)}
                            />
                        ) : editingText ? (
                            <div className="flex gap-2">
                                <textarea
                                    className="ticker-textarea bg-slate-800/50 border border-slate-600 rounded-lg p-2 w-full text-sm"
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    rows={2}
                                />
                                <div className="flex flex-col gap-1">
                                    <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={handleSaveTextEdit} disabled={saving}>
                                        {saving ? '…' : '✓'}
                                    </button>
                                    <button type="button" className="ticker-action-icon" onClick={() => setEditingText(false)}>✕</button>
                                </div>
                            </div>
                        ) : (
                            <p className="ticker-event-text">{displayText || <span className="text-slate-500 italic">Kein Text</span>}</p>
                        )}

                        {!compact && !editingStat && (
                            <div className="ticker-event-flags">
                                <button
                                    type="button"
                                    className={`ticker-flag-btn ${event.reportFlags?.useInReport ? 'ticker-flag-btn--on' : ''}`}
                                    onClick={() => handleToggleReportFlag('useInReport')}
                                >
                                    📝 Bericht
                                </button>
                                <button
                                    type="button"
                                    className={`ticker-flag-btn ${event.reportFlags?.isKeyScene ? 'ticker-flag-btn--star-on' : ''}`}
                                    onClick={() => handleToggleReportFlag('isKeyScene')}
                                >
                                    ⭐ Schlüsselszene
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="ticker-event-actions">
                {compact && !editingStat && (
                    <div className="ticker-event-flags ticker-event-flags--hover">
                        <button type="button" className={`ticker-flag-btn ${event.reportFlags?.useInReport ? 'ticker-flag-btn--on' : ''}`} onClick={() => handleToggleReportFlag('useInReport')}>📝</button>
                        <button type="button" className={`ticker-flag-btn ${event.reportFlags?.isKeyScene ? 'ticker-flag-btn--star-on' : ''}`} onClick={() => handleToggleReportFlag('isKeyScene')}>⭐</button>
                    </div>
                )}
                {!editingStat && (
                    <>
                        <button type="button" className="ticker-action-icon" onClick={handleTogglePublish} title={event.published ? 'Veröffentlicht' : 'Entwurf'}>
                            {event.published ? '👁' : '👁‍🗨'}
                        </button>
                        <button type="button" className="ticker-action-icon" onClick={startEdit} title={isStatEvent ? 'Spieler/Wechsel bearbeiten' : 'Text bearbeiten'}>
                            {isStatEvent ? '👤' : '✏️'}
                        </button>
                        <button type="button" className="ticker-action-icon ticker-action-icon--danger" onClick={handleDelete} title="Löschen">🗑</button>
                    </>
                )}
            </div>
        </article>
    );
}

